import { formatEuros } from '../format.ts';
import type { ExtraInvoice, Party } from '../pages/extra/business/types.ts';

const fmtDate = (iso: string) => new Date(iso.length === 10 ? `${iso}T12:00:00` : iso).toLocaleDateString('fr-FR');

const METHOD_LABELS: Record<string, string> = {
  PLATEFORME: 'via Mon Extra',
  VIREMENT: 'virement',
  CHEQUE: 'chèque',
  ESPECES: 'espèces',
  CARTE: 'carte bancaire',
  AUTRE: 'autre mode',
};

function PartyBlock({ title, party, issuer }: { title: string; party: Party; issuer?: boolean }) {
  return (
    <div style={{ flex: 1, minWidth: 220 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-tertiary)', textTransform: 'uppercase', marginBottom: 6 }}>{title}</div>
      <div style={{ fontWeight: 800 }}>{party.name}{issuer ? ', EI' : ''}</div>
      {party.tradeName && <div style={{ fontSize: 13 }}>{party.tradeName}</div>}
      <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
        {party.address && <>{party.address}<br /></>}
        {(party.postalCode || party.city) && <>{party.postalCode} {party.city}<br /></>}
        {party.siret && <>SIRET {party.siret}</>}
        {party.vatNumber ? <><br />TVA {party.vatNumber}</> : null}
      </div>
    </div>
  );
}

export function ExtraInvoiceView({ invoice }: { invoice: ExtraInvoice }) {
  const credit = invoice.kind === 'CREDIT_NOTE';
  const franchise = invoice.issuer.vatRegime === 'FRANCHISE';
  return (
    <div>
      <div className="no-print" style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 14, flexWrap: 'wrap' }}>
        <button className="btn btn-primary btn-sm" onClick={() => window.print()}>Imprimer / enregistrer en PDF</button>
        <a className="btn btn-secondary btn-sm" style={{ display: 'inline-flex' }} href={`/api/extra/invoices/${invoice.id}/export.xml`}>Format structuré (XML)</a>
        {credit ? (
          <span className="badge badge-neutral">Avoir sur la facture {invoice.creditedNumber}</span>
        ) : invoice.creditNoteNumber ? (
          <span className="badge badge-unavailable">Annulée par l'avoir {invoice.creditNoteNumber}</span>
        ) : invoice.status === 'PAID' ? (
          <span className="badge badge-available">Payée le {invoice.paidAt ? fmtDate(invoice.paidAt) : ''} · {METHOD_LABELS[invoice.paidMethod ?? ''] ?? invoice.paidMethod}</span>
        ) : (
          <span className="badge badge-pending">En attente de paiement</span>
        )}
      </div>

      <div className="card invoice-sheet" style={{ padding: 28, display: 'flex', flexDirection: 'column', gap: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: 22, fontWeight: 800 }}>{credit ? 'AVOIR' : 'FACTURE'}</div>
            <div style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>Prestation de services</div>
          </div>
          <div style={{ textAlign: 'right', fontSize: 13 }}>
            <div style={{ fontWeight: 800 }}>N° {invoice.number}</div>
            <div>Date d'émission : {fmtDate(invoice.issuedAt)}</div>
            <div>Date de la prestation : {fmtDate(invoice.serviceDate)}</div>
            {!credit && <div>Échéance : {invoice.status === 'PAID' ? 'réglée' : invoice.dueDate ? fmtDate(invoice.dueDate) : 'à réception'}</div>}
          </div>
        </div>

        <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
          <PartyBlock title="Prestataire" party={invoice.issuer} issuer />
          <PartyBlock title="Client" party={invoice.client} />
        </div>

        {credit && (
          <div style={{ fontSize: 13, lineHeight: 1.6 }}>
            Avoir annulant intégralement la facture <strong>{invoice.creditedNumber}</strong>. Motif : {invoice.creditReason}.
          </div>
        )}

        <div className="table-wrap" style={{ border: 'none' }}>
          <table className="data-table">
            <thead>
              <tr><th>Désignation</th><th style={{ textAlign: 'right' }}>Qté</th><th style={{ textAlign: 'right' }}>PU HT</th><th style={{ textAlign: 'right' }}>Total HT</th></tr>
            </thead>
            <tbody>
              {invoice.lines.map((l, i) => (
                <tr key={i}>
                  <td>{l.description}</td>
                  <td style={{ textAlign: 'right' }}>{String(l.quantity).replace('.', ',')}</td>
                  <td style={{ textAlign: 'right' }}>{formatEuros(l.unitPriceCents)}</td>
                  <td style={{ textAlign: 'right' }}>{formatEuros(l.totalCents)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div style={{ alignSelf: 'flex-end', minWidth: 260, display: 'flex', flexDirection: 'column', gap: 6, fontSize: 14 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Total HT</span><span>{formatEuros(invoice.subtotalCents)}</span></div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span>TVA {invoice.vatRatePct > 0 ? `${String(invoice.vatRatePct).replace('.', ',')} %` : ''}</span><span>{formatEuros(invoice.vatCents)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 800, fontSize: 16, borderTop: '1px solid var(--border)', paddingTop: 8 }}>
            <span>Total TTC</span><span>{formatEuros(invoice.totalCents)}</span>
          </div>
        </div>

        <div style={{ fontSize: 11.5, color: 'var(--text-secondary)', lineHeight: 1.6, borderTop: '1px solid var(--border)', paddingTop: 14 }}>
          {franchise && <div>TVA non applicable, art. 293 B du CGI.</div>}
          <div>Nature de l'opération : prestation de services.{invoice.client.siret ? ` SIREN du client : ${invoice.client.siret.slice(0, 9)}.` : ''}</div>
          {invoice.issuedVia.mode === 'MANDATE' ? (
            <div>
              Facture établie par {invoice.issuedVia.platformName} ({invoice.issuedVia.site}) au nom et pour le compte de {invoice.issuer.name}, en vertu du
              mandat de facturation accepté le {invoice.mandateAcceptedAt ? fmtDate(invoice.mandateAcceptedAt) : ''}.
            </div>
          ) : (
            <div>Facture établie par {invoice.issuer.name} avec l'outil de facturation de {invoice.issuedVia.site}.</div>
          )}
          {invoice.status !== 'PAID' && !credit && !invoice.creditNoteNumber && (
            <div>
              En cas de retard de paiement : pénalités égales à trois fois le taux d'intérêt légal et indemnité forfaitaire de recouvrement de 40 €.
              Pas d'escompte pour paiement anticipé.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
